import { FC, memo, SyntheticEvent } from 'react';
import { css } from '@emotion/react';
import dayjs from 'dayjs';
import { useQueryClient } from 'react-query';
import { TaskCenterTypes, useTaskCenterMutations } from '@frontend/api-task-center';
import { getUser } from '@frontend/auth-helpers';
import { useTranslation } from '@frontend/i18n';
import { Icon } from '@frontend/icons';
import {
  useAlert,
  Text,
  Chip,
  usePopoverMenu,
  Button,
  SpinningLoader,
  NakedButton,
  Avatar,
  useModalControl,
  ConfirmationModal,
} from '@frontend/design-system';
import { useTaskCenterStore } from '../../../provider';
import { taskCenterTrackingIds } from '../../../tracking-ids';
import { getFullName } from '../../../utils';
import { StatusUpdater } from '../status-updater/status-updater';
import { TaskItemPopoverMenu } from '../task-item/task-item-popover-menu';
import { containerStyle, dueDateChipStyle, loaderStyles, taskTitleContainer } from './task-item.styles';
import { TaskPriority } from './task-priority';

export const TaskItem: FC<TaskCenterTypes.Task> = memo(
  ({ dueDate, title, status, id, locationId, priority, ...rest }) => {
    const { locationsData, setTaskMode, setSelectedTask, assignees } = useTaskCenterStore([
      'locationsData',
      'setTaskMode',
      'setSelectedTask',
      'assignees',
    ]);
    const { t } = useTranslation('task-center');
    const user = getUser();
    const queryClient = useQueryClient();
    const alert = useAlert();
    const { modalProps, openModal } = useModalControl();

    const date = dueDate ? dayjs(dueDate).format('MMMM D, YYYY') : t('No Due Date');
    const location: string = locationsData.find((loc) => loc.id === locationId)?.name || 'Location Name';
    const assignee = assignees.find((assign) => assign.userId === rest.assignee);
    const assigneeName = getFullName(assignee);

    const { getTriggerProps, getMenuProps, getItemProps, close } = usePopoverMenu();
    const { taskDeleteMutation, taskUpdateMutation } = useTaskCenterMutations();
    const { mutate: updateTask, isLoading: isUpdating } = taskUpdateMutation;
    const { mutate: deleteTaskMutation, isLoading: isDeleting } = taskDeleteMutation;
    const isLoading = isDeleting || isUpdating;

    const onClickTask = () => {
      setSelectedTask({ dueDate, title, status, id, locationId, priority, ...rest });
      setTaskMode('details');
      close();
    };

    const onClickEdit = () => {
      setSelectedTask({ dueDate, title, status, id, locationId, priority, ...rest });
      setTaskMode('edit');
      close();
    };

    const updateTaskStatus = (newStatus: TaskCenterTypes.TaskStatus) => {
      if (id && user?.userID) {
        updateTask(
          { id, status: newStatus, updatedBy: user.userID },
          {
            onError: () => {
              close();
              alert.error(t('Failed to update task'));
            },
            onSuccess: () => {
              close();
              queryClient.invalidateQueries(['task-center', status]);
              queryClient.invalidateQueries(['task-center', newStatus]);
            },
          }
        );
      }
    };

    const deleteTask = () => {
      if (id) {
        deleteTaskMutation(
          { id },
          {
            onError: () => {
              close();
              alert.error(t('Failed to delete task'));
            },
            onSuccess: () => {
              close();
              alert.success(t('Deleted task successfully'));
              queryClient.invalidateQueries(['task-center', status]);
            },
          }
        );
      }
    };

    return (
      <NakedButton
        css={containerStyle}
        onClick={onClickTask}
        trackingId={taskCenterTrackingIds.openTaskDetails}
        aria-label={t('Open task details')}
      >
        <div
          className='row'
          css={css`
            align-items: flex-start !important;
          `}
        >
          <StatusUpdater
            status={status}
            onAction={(e: SyntheticEvent<HTMLButtonElement>) => {
              e.stopPropagation();
              updateTaskStatus(TaskCenterTypes.TaskStatus.STATUS_COMPLETED);
            }}
          />
          <Text textAlign='left' size='large' weight='bold' css={taskTitleContainer}>
            {title}
          </Text>
          {rest.source === TaskCenterTypes.TaskSource.SOURCE_CALL_INTELLIGENCE && <Icon name='aiassistant' size={16} />}
          {status && (
            <>
              <Button
                iconName='more'
                variant='secondary'
                {...getTriggerProps()}
                hoverLabel={t('More options')}
                trackingId={taskCenterTrackingIds.openTaskOptions}
              />
              <TaskItemPopoverMenu
                status={status}
                updateTaskStatus={updateTaskStatus}
                deleteTask={() => {
                  close();
                  openModal();
                }}
                getMenuProps={getMenuProps}
                getItemProps={getItemProps}
                onClickEdit={onClickEdit}
              />
            </>
          )}
        </div>
        <div className='row'>
          {priority && <TaskPriority priority={priority} />}
          <Chip
            leftElement={<Icon name='calendar-schedule-back' size={16} color='default' />}
            variant='neutral'
            maxWidth='100%'
            css={dueDateChipStyle}
          >
            {date}
          </Chip>
          <Chip leftElement={<Icon name='location' size={16} />} variant='neutral'>
            {location}
          </Chip>
          {assigneeName && (
            <Chip variant='neutral' leftElement={<Avatar size='xxs' isUser name={assigneeName} />}>
              {assigneeName}
            </Chip>
          )}
        </div>
        <ConfirmationModal
          title={t('Are you sure you want to delete the task?')}
          onConfirm={deleteTask}
          destructive
          {...modalProps}
        />
        {isLoading && (
          <div css={loaderStyles}>
            <SpinningLoader size='large' />
          </div>
        )}
      </NakedButton>
    );
  }
);

TaskItem.displayName = 'TaskItem';
